/**
 * Quick entry: turn a plain sentence into ledger / maintenance / rent records.
 *
 *   "today we repaired bathroom sink, cost 500 by contractor cesar at dunran"
 *     → Ledger: −$500 Repair & maintenance at 3441 Dunran (Cesar)
 *     → Maintenance: "Repaired bathroom sink" — done, cost $500
 *
 * Pure and rule-based (runs in the browser, no AI key needed). The owner sees
 * a preview and can fix any field before saving.
 */
import { inferExpenseCategory } from "./workbookImport.js";

const DAY = 86400000;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const STOP = new Set([
  "the", "and", "for", "with", "from", "that", "this", "today", "yesterday", "cost", "costs", "paid", "pay",
  "rent", "house", "unit", "road", "drive", "court", "street", "avenue", "circle", "lane", "way", "contractor",
  "repair", "repaired", "fixed", "plumbing", "handyman", "company", "services", "llc", "inc", "and", "drain",
]);
const STREET_WORDS = /^(rd|road|dr|drive|ct|court|st|street|ave|avenue|cir|circle|ln|lane|way|blvd|pl|place)\.?$/i;

const pad = (n) => String(n).padStart(2, "0");
const toDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const words = (text) => String(text ?? "").toLowerCase().match(/[a-z0-9']+/g) || [];

function editDistanceAtMost1(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
    } else {
      edits += 1;
      if (edits > 1) return false;
      if (a.length > b.length) i += 1;
      else if (b.length > a.length) j += 1;
      else {
        i += 1;
        j += 1;
      }
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

const wordMatches = (token, word) =>
  token === word || (word.length >= 5 && token.length >= 4 && editDistanceAtMost1(token, word));

/** Find the property the sentence mentions (street name, house number, or name). */
function matchProperty(text, properties) {
  const tokens = words(text);
  let best = null;
  for (const property of properties) {
    const label = `${property.name || ""} ${property.address || ""}`;
    const parts = words(label);
    const number = parts.find((p) => /^\d+$/.test(p));
    const streetWords = parts.filter((p) => !/^\d+$/.test(p) && !STREET_WORDS.test(p) && p.length >= 3);
    let score = 0;
    let matched = [];
    for (const street of new Set(streetWords)) {
      const hit = tokens.find((t) => wordMatches(t, street));
      if (hit) {
        score += 2;
        matched.push(hit);
      }
    }
    if (number && tokens.includes(number)) {
      score += 2;
      matched.push(number);
    }
    if (score && (!best || score > best.score)) best = { property, score, matched };
  }
  return best;
}

function matchPerson(text, people, nameOf) {
  const tokens = words(text).filter((t) => t.length >= 3 && !STOP.has(t));
  let best = null;
  for (const person of people) {
    const parts = words(nameOf(person)).filter((p) => p.length >= 3 && !STOP.has(p));
    let score = 0;
    const matched = [];
    for (const part of new Set(parts)) {
      const hit = tokens.find((t) => t === part || (part.length >= 5 && editDistanceAtMost1(t, part)));
      if (hit) {
        score += 1;
        matched.push(hit);
      }
    }
    if (score && (!best || score > best.score)) best = { person, score, matched };
  }
  return best;
}

const FUTURE_RE = /\b(need(?:s)? to|have to|has to|must|should|will|gonna|going to|plan(?:ned)? to|schedule(?:d)?|remind(?: me)?|appointment|meet(?:ing)?|inspection|visit|showing|call|text|email|follow up|todo|to do|tomorrow|next|upcoming|due)\b/i;

/** Date words → "YYYY-MM-DD" plus the text that expressed it. Weekdays point forward when the sentence plans ahead. */
function matchDate(text, now) {
  const lower = text.toLowerCase();
  const ahead = FUTURE_RE.test(lower);
  let m = lower.match(/\bday after tomorrow\b/);
  if (m) return { day: toDay(new Date(now.getTime() + 2 * DAY)), span: m[0] };
  m = lower.match(/\btomorrow\b/);
  if (m) return { day: toDay(new Date(now.getTime() + DAY)), span: m[0] };
  m = lower.match(/\bin (\d{1,2}) days?\b/);
  if (m) return { day: toDay(new Date(now.getTime() + Number(m[1]) * DAY)), span: m[0] };
  m = lower.match(/\bnext week\b/);
  if (m) return { day: toDay(new Date(now.getTime() + 7 * DAY)), span: m[0] };
  m = lower.match(/\b(?:on |by |this |next )?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  if (m && (ahead || /^(this|next|by)/.test(m[0])) && !/\blast\b/.test(lower)) {
    const target = WEEKDAYS.indexOf(m[1]);
    let diff = (target - now.getDay() + 7) % 7;
    if (diff === 0) diff = 7;
    return { day: toDay(new Date(now.getTime() + diff * DAY)), span: m[0] };
  }
  m = lower.match(/\b(today|tonight|this morning|this afternoon)\b/);
  if (m) return { day: toDay(now), span: m[0] };
  m = lower.match(/\byesterday\b/);
  if (m) return { day: toDay(new Date(now.getTime() - DAY)), span: m[0] };
  m = lower.match(/\b(\d{1,2}) days? ago\b/);
  if (m) return { day: toDay(new Date(now.getTime() - Number(m[1]) * DAY)), span: m[0] };
  m = lower.match(/\b(?:on |last )?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  if (m) {
    const target = WEEKDAYS.indexOf(m[1]);
    let diff = (now.getDay() - target + 7) % 7;
    if (diff === 0) diff = 7;
    return { day: toDay(new Date(now.getTime() - diff * DAY)), span: m[0] };
  }
  m = lower.match(/\b(?:on )?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
  if (m) {
    const year = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : now.getFullYear();
    return { day: toDay(new Date(year, Number(m[1]) - 1, Number(m[2]))), span: m[0] };
  }
  m = lower.match(/\b(?:on )?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? (\d{1,2})(?:,? (\d{4}))?\b/);
  if (m) {
    const year = m[3] ? Number(m[3]) : now.getFullYear();
    return { day: toDay(new Date(year, MONTHS.indexOf(m[1]), Number(m[2]))), span: m[0] };
  }
  return { day: toDay(now), span: "" };
}

/** Money: "$500", "500 dollars", "cost 500", "paid 1,700.00". */
function matchAmount(text) {
  const patterns = [
    /\$\s?(\d[\d,]*(?:\.\d{1,2})?)/,
    /\b(\d[\d,]*(?:\.\d{1,2})?)\s?(?:dollars|usd|bucks)\b/i,
    /\b(?:cost|costs|costing|paid|pay|spent|charged|price|total|amount|for|was|of)\s+(?:of\s+|was\s+|is\s+|about\s+|around\s+)?(\d[\d,]*(?:\.\d{1,2})?)\b/i,
  ];
  for (const pattern of patterns) {
    const m = text.match(pattern);
    if (m) {
      const amount = Number.parseFloat(m[1].replace(/,/g, ""));
      if (amount > 0) return { amount, span: m[0] };
    }
  }
  // Fallback: the last plain number left (house numbers and dates are already removed).
  const numbers = [...text.matchAll(/(?<![\w/])(\d[\d,]*(?:\.\d{1,2})?)(?![\w/])/g)].filter((m) => {
    const value = Number.parseFloat(m[1].replace(/,/g, ""));
    return value > 0 && !(Number.isInteger(value) && value >= 1990 && value <= 2100 && !m[1].includes(","));
  });
  const last = numbers[numbers.length - 1];
  if (last) return { amount: Number.parseFloat(last[1].replace(/,/g, "")), span: last[0] };
  return { amount: null, span: "" };
}

const DONE_RE = /\b(repaired|fixed|replaced|installed|painted|cleaned|serviced|unclogged|patched|completed|finished|done|paid (?:the )?(?:plumber|handyman|contractor|electrician))\b/i;
const NEEDED_RE = /\b(broken|leak(?:ing|s|y)?|not working|doesn'?t work|won'?t|needs?|clogged|issue|problem|request(?:ed)?|out of order|stopped working|cracked)\b/i;
const REPAIR_WORDS = /\b(repair|fix|replace|install|paint|clean|service|unclog|patch|plumb|leak|sink|toilet|faucet|heater|furnace|hvac|ac\b|a\/c|roof|gutter|window|door|lock|appliance|dryer|washer|fridge|stove|oven|dishwasher|disposal|fan|outlet|light|electric|mold|pest|termite|carpet|floor|wall|ceiling)/i;
const RENT_RE = /\b(rent)\b/i;
const RECEIVED_RE = /\b(paid|received|collected|got|sent|deposited)\b/i;

function cleanDescription(text, spans) {
  let out = ` ${text} `;
  for (const span of spans.filter(Boolean)) {
    out = out.replace(new RegExp(span.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), " ");
  }
  out = out
    .replace(/\b(by|from|with|via|using)\s+(the\s+)?(contractor|plumber|handyman|electrician|guy)?\s*$/i, " ")
    .replace(/\b(by|from|with|via)\s+(the\s+)?(contractor|plumber|handyman|electrician)\b/gi, " ")
    .replace(/\b(at|for|on|in)\s+(the\s+)?(house|property|unit)?\s*(?=[,.;]|\s*$)/gi, " ")
    .replace(/\b(contractor|cost|costs|costing|total|amount)\b/gi, " ")
    .replace(/^\s*(we|i|they|he|she|today|tonight)\b/i, " ")
    .replace(/^\s*(need(?:s)? to|have to|has to|must|should|will|gonna|going to|remind me to)\b/i, " ")
    .replace(/\b(at|for|by|with|from|on|in)\s+(?=(at|for|by|with|from|on|in|to)\b|[,.;])/gi, " ")
    .replace(/\b(at|for|by|with|from|on|in)\s+(?=(at|for|by|with|from|on|in|to)\b|[,.;])/gi, " ")
    .replace(/\s+[,.;:]/g, ",")
    .replace(/[,.;:\s]+$/g, "")
    .replace(/(\s+(at|for|on|in|by|from|with|to|of)\b)+\s*$/i, "")
    .replace(/[,.;:\s]+$/g, "")
    .replace(/^[,.;:\s]+/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return out ? out.charAt(0).toUpperCase() + out.slice(1) : "";
}

/**
 * Parse one line. Returns a draft the UI can edit:
 *   { kind: "repair" | "repairRequest" | "rent" | "expense" | "unknown",
 *     day, propertyId, amount, contractorId, tenantId, description, category,
 *     createExpense, createJob, jobDone, notes: [] }
 */
export function parseQuickEntry(text, { properties = [], contractors = [], tenants = [] } = {}, now = new Date()) {
  const raw = String(text ?? "").trim();
  const notes = [];
  const date = matchDate(raw, now);
  const property = matchProperty(raw, properties);
  // Strip matched house text before reading the amount, so "3441 Dunran" isn't $3,441.
  let money = raw;
  for (const token of property?.matched || []) money = money.replace(new RegExp(`\\b${token}\\b`, "i"), " ");
  if (date.span) money = money.replace(date.span, " ");
  const amount = matchAmount(money);
  const contractor = matchPerson(raw, contractors, (c) => `${c.contactName || ""} ${c.companyName || ""}`);
  const tenant = matchPerson(raw, tenants.filter((t) => t.isActive !== false), (t) => `${t.firstName || ""} ${t.lastName || ""}`);

  const future = date.day > toDay(now);
  let kind = "expense";
  if (future || (FUTURE_RE.test(raw) && !amount.amount && !DONE_RE.test(raw))) {
    kind = REPAIR_WORDS.test(raw) || NEEDED_RE.test(raw) ? "repairRequest" : "todo";
  } else if (RENT_RE.test(raw) && (RECEIVED_RE.test(raw) || tenant)) kind = "rent";
  else if (DONE_RE.test(raw) && (REPAIR_WORDS.test(raw) || contractor)) kind = "repair";
  else if (NEEDED_RE.test(raw) && !amount.amount) kind = "repairRequest";
  else if (REPAIR_WORDS.test(raw) && contractor) kind = "repair";
  else if (!amount.amount) kind = REPAIR_WORDS.test(raw) ? "repairRequest" : "unknown";

  let propertyId = property ? String(property.property._id) : "";
  let tenantId = "";
  if (kind === "rent" && tenant) {
    tenantId = String(tenant.person._id);
    if (!propertyId) propertyId = String(tenant.person.propertyId || "");
  }
  if (kind === "rent" && !amount.amount && tenant?.person?.monthlyRent) {
    amount.amount = Number(tenant.person.monthlyRent);
    notes.push("Used the tenant's monthly rent as the amount.");
  }

  const spans = [date.span, amount.span, ...(property?.matched || []), ...(contractor?.matched || [])];
  if (kind === "rent") spans.push(...(tenant?.matched || []));
  let description = cleanDescription(raw, spans);
  if (kind === "rent") description = tenant ? `Rent · ${tenant.person.firstName} ${tenant.person.lastName}`.trim() : description || "Rent";

  const category =
    kind === "rent" ? "rent" : kind === "repair" || kind === "repairRequest" ? "maintenance" : inferExpenseCategory(raw);

  if (!propertyId && !["expense", "todo"].includes(kind)) notes.push("Which house? Pick one below.");
  if ((kind === "repair" || kind === "expense" || kind === "rent") && !amount.amount) notes.push("No amount found — type one below.");

  return {
    kind,
    day: date.day,
    propertyId,
    amount: amount.amount ? String(amount.amount) : "",
    contractorId: contractor ? String(contractor.person._id) : "",
    tenantId,
    description: description || raw,
    category,
    createExpense: kind === "repair" || kind === "expense",
    createJob: kind === "repair" || kind === "repairRequest",
    jobDone: kind === "repair",
    scheduled: future,
    notes,
  };
}

/** Split a message into separate entries: one per line (or "; "). */
export function splitEntries(text) {
  return String(text ?? "")
    .split(/\n|;\s+/)
    .map((line) => line.trim())
    .filter(Boolean);
}
